import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  ChevronUp,
  ChevronDown,
  Plus,
  Trash2,
  AlignLeft,
  AlignJustify,
  Hash,
  Circle,
  CheckSquare,
  Flag,
  AlertTriangle,
} from 'lucide-react';
import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import { api } from '../api/client.js';
import { useSession } from '../auth/SessionContext.jsx';

const TYPE_META = {
  text:        { label: 'Texte court',     Icon: AlignLeft,    color: '#5d6677', hint: 'Une ligne de texte (ex : prénom, profondeur)' },
  textarea:    { label: 'Texte long',      Icon: AlignJustify, color: '#5d6677', hint: 'Plusieurs lignes (ex : commentaire détaillé)' },
  number:      { label: 'Nombre',          Icon: Hash,         color: '#5d6677', hint: 'Valeur numérique uniquement' },
  select:      { label: 'Choix unique',    Icon: Circle,       color: '#2EB6A7', hint: "L'utilisateur sélectionne UNE réponse parmi la liste" },
  multiselect: { label: 'Choix multiples', Icon: CheckSquare,  color: '#1A2849', hint: "L'utilisateur peut cocher PLUSIEURS réponses" },
  flag:        { label: 'Case à cocher',   Icon: Flag,         color: '#c0392b', hint: 'Une case Oui/Non isolée' },
};

const TYPE_ORDER = ['select', 'multiselect', 'text', 'textarea', 'number', 'flag'];

function TypeBadge({ type }) {
  const meta = TYPE_META[type] || TYPE_META.text;
  const Icon = meta.Icon;
  return (
    <span className="vrt-survey__type-badge" style={{ background: meta.color }}>
      <Icon size={12} strokeWidth={2.5} />
      {meta.label}
    </span>
  );
}

export default function AdminSurvey() {
  const { session } = useSession();

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingQuestion, setAddingQuestion] = useState(false);
  const [newForm, setNewForm] = useState({ label: '', type: 'select', required: false, is_priority_flag: false, options: [''] });

  if (session && session.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  const loadQuestions = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get('/api/admin/survey/questions');
      const sorted = (data?.questions || []).sort((a, b) => (a.position || 0) - (b.position || 0));
      setQuestions(sorted);
    } catch (e) {
      setError(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadQuestions(); }, []);

  // Auto-save (avec debounce simple par champ)
  const debounceMap = new Map();
  const scheduleSave = (id, field, value, persist) => {
    const key = `${id}_${field}`;
    if (debounceMap.has(key)) clearTimeout(debounceMap.get(key));
    debounceMap.set(key, setTimeout(async () => {
      try {
        await persist(value);
      } catch (e) {
        setError(e.message || 'Erreur d’enregistrement');
      }
    }, 500));
  };

  const onQuestionChange = (id, field, value) => {
    setQuestions(prev => prev.map(q => q.id === id ? { ...q, [field]: value } : q));
    if (['label', 'type', 'required', 'is_priority_flag'].includes(field)) {
      scheduleSave(id, field, value, async (v) => {
        await api.patch(`/api/admin/survey/questions/${id}`, { [field]: v });
      });
    }
  };

  const onOptionLabelChange = (qid, oid, value) => {
    setQuestions(prev => prev.map(q =>
      q.id === qid
        ? { ...q, options: q.options.map(o => o.id === oid ? { ...o, label: value } : o) }
        : q
    ));
    scheduleSave(oid, 'label', value, async (v) => {
      await api.patch(`/api/admin/survey/options/${oid}`, { label: v });
    });
  };

  const onAddOption = async (id) => {
    try {
      const res = await api.post(`/api/admin/survey/questions/${id}/options`, {
        label: 'Nouvelle réponse',
      });
      setQuestions(prev => prev.map(q =>
        q.id === id ? { ...q, options: [...(q.options || []), res] } : q
      ));
    } catch (e) {
      setError(e.message || 'Erreur création réponse');
    }
  };

  const onDeleteOption = async (qid, oid) => {
    if (!window.confirm('Supprimer cette réponse ?')) return;
    try {
      await api.del(`/api/admin/survey/options/${oid}`);
      setQuestions(prev => prev.map(q =>
        q.id === qid ? { ...q, options: q.options.filter(o => o.id !== oid) } : q
      ));
    } catch (e) {
      setError(e.message || 'Erreur suppression réponse');
    }
  };

  const onMoveQuestion = async (index, direction) => {
    const newIdx = direction === 'up' ? index - 1 : index + 1;
    if (newIdx < 0 || newIdx >= questions.length) return;

    const reordered = [...questions];
    [reordered[index], reordered[newIdx]] = [reordered[newIdx], reordered[index]];
    setQuestions(reordered);

    try {
      const ids = reordered.map(q => q.id);
      await api.post('/api/admin/survey/reorder-questions', { ids });
    } catch (e) {
      setError(e.message || 'Erreur réordonnage');
      setQuestions(questions);
    }
  };

  const onDeleteQuestion = async (id, label) => {
    if (!window.confirm(`Supprimer la question « ${label || ''} » et toutes ses réponses ?\n\nCette action est définitive.`)) return;
    try {
      await api.del(`/api/admin/survey/questions/${id}`);
      setQuestions(questions.filter(q => q.id !== id));
    } catch (e) {
      setError(e.message || 'Erreur suppression');
    }
  };

  const showOptionalTypes = newForm.type === 'select' || newForm.type === 'multiselect';

  const onAddNewOption = () => {
    setNewForm(prev => ({ ...prev, options: [...prev.options, ''] }));
  };

  const onChangeNewOption = (idx, value) => {
    setNewForm(prev => ({ ...prev, options: prev.options.map((o, i) => i === idx ? value : o) }));
  };

  const onRemoveNewOption = (idx) => {
    setNewForm(prev => ({ ...prev, options: prev.options.filter((_, i) => i !== idx) }));
  };

  const onAddQuestion = async (e) => {
    e.preventDefault();
    if (!newForm.label.trim()) {
      setError('Le libellé est requis');
      return;
    }
    try {
      const body = {
        label: newForm.label.trim(),
        type: newForm.type,
        required: newForm.required,
        is_priority_flag: newForm.is_priority_flag,
      };
      const cleanOpts = (newForm.options || []).map(s => (s || '').trim()).filter(Boolean);
      if (showOptionalTypes && cleanOpts.length > 0) {
        body.options = cleanOpts.map(label => ({ label }));
      }
      const res = await api.post('/api/admin/survey/questions', body);
      setQuestions([...questions, res].sort((a, b) => (a.position || 0) - (b.position || 0)));
      setNewForm({ label: '', type: 'select', required: false, is_priority_flag: false, options: [''] });
      setAddingQuestion(false);
    } catch (e) {
      setError(e.message || 'Erreur création question');
    }
  };

  return (
    <div className="vrt-survey">
      <Header />

      <main className="vrt-survey__main">
        <header className="vrt-survey__top">
          <div>
            <h1 className="vrt-survey__title">Configuration du formulaire</h1>
            <p className="vrt-survey__lead">
              Personnalise les questions que tes équipes voient sur le terrain.
              Tout est enregistré automatiquement.
            </p>
          </div>
          <div className="vrt-survey__top-stats">
            <span className="vrt-survey__count">{questions.length}</span>
            <span className="vrt-survey__count-label">question{questions.length > 1 ? 's' : ''}</span>
          </div>
        </header>

        {error && (
          <div className="vrt-survey__error" role="alert">
            <AlertTriangle size={16} /> {error}
            <button
              type="button"
              className="vrt-survey__error-close"
              onClick={() => setError(null)}
              aria-label="Fermer"
            >
              ✕
            </button>
          </div>
        )}

        {loading ? (
          <div className="vrt-survey__empty">Chargement…</div>
        ) : questions.length === 0 ? (
          <div className="vrt-survey__empty">Aucune question pour le moment. Ajoute-en une ci-dessous.</div>
        ) : (
          <div className="vrt-survey__list">
            {questions.map((q, idx) => {
              const meta = TYPE_META[q.type] || TYPE_META.text;
              const hasOptions = q.type === 'select' || q.type === 'multiselect';
              return (
                <article
                  key={q.id}
                  className={`vrt-survey__card ${q.is_priority_flag ? 'vrt-survey__card--priority' : ''}`}
                  style={{ '--accent': q.is_priority_flag ? '#c0392b' : meta.color }}
                >
                  <div className="vrt-survey__qhead">
                    <span className="vrt-survey__qnum">Q{idx + 1}</span>
                    <TypeBadge type={q.type} />
                    {q.required && <span className="vrt-survey__chip">Obligatoire</span>}
                    {q.is_priority_flag && <span className="vrt-survey__chip vrt-survey__chip--alert">Alerte rouge sur carte</span>}

                    <div className="vrt-survey__qactions">
                      <button
                        type="button"
                        className="vrt-survey__icon-btn"
                        onClick={() => onMoveQuestion(idx, 'up')}
                        disabled={idx === 0}
                        aria-label="Monter"
                        title="Monter"
                      >
                        <ChevronUp size={18} />
                      </button>
                      <button
                        type="button"
                        className="vrt-survey__icon-btn"
                        onClick={() => onMoveQuestion(idx, 'down')}
                        disabled={idx === questions.length - 1}
                        aria-label="Descendre"
                        title="Descendre"
                      >
                        <ChevronDown size={18} />
                      </button>
                      <button
                        type="button"
                        className="vrt-survey__icon-btn vrt-survey__icon-btn--danger"
                        onClick={() => onDeleteQuestion(q.id, q.label)}
                        aria-label="Supprimer la question"
                        title="Supprimer la question"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  <textarea
                    value={q.label || ''}
                    onChange={(e) => onQuestionChange(q.id, 'label', e.target.value)}
                    className="vrt-survey__qlabel"
                    rows="2"
                    placeholder="Quelle est la question posée ?"
                  />

                  <div className="vrt-survey__qcontrols">
                    <label className="vrt-survey__field">
                      <span>Type de réponse</span>
                      <select
                        value={q.type || 'text'}
                        onChange={(e) => onQuestionChange(q.id, 'type', e.target.value)}
                        className="vrt-input"
                      >
                        {TYPE_ORDER.map(t => (
                          <option key={t} value={t}>{TYPE_META[t].label}</option>
                        ))}
                      </select>
                      <small className="vrt-survey__hint">{meta.hint}</small>
                    </label>

                    <label className="vrt-survey__toggle">
                      <input
                        type="checkbox"
                        checked={q.required || false}
                        onChange={(e) => onQuestionChange(q.id, 'required', e.target.checked)}
                      />
                      <span>Réponse obligatoire</span>
                    </label>

                    <label className={`vrt-survey__toggle ${q.is_priority_flag ? 'vrt-survey__toggle--alert' : ''}`}>
                      <input
                        type="checkbox"
                        checked={q.is_priority_flag || false}
                        onChange={(e) => onQuestionChange(q.id, 'is_priority_flag', e.target.checked)}
                      />
                      <span>
                        Déclencher l’alerte rouge sur la carte
                      </span>
                    </label>
                  </div>

                  {q.is_priority_flag && (
                    <div className="vrt-survey__warning">
                      <AlertTriangle size={14} />
                      Les avaloirs cochant cette case apparaîtront en rouge sur la carte. Une seule question peut activer l’alerte.
                    </div>
                  )}

                  {hasOptions && (
                    <div className="vrt-survey__options">
                      <div className="vrt-survey__options-title">Réponses possibles</div>
                      {(q.options || []).length === 0 && (
                        <div className="vrt-survey__options-empty">Aucune réponse pour le moment.</div>
                      )}
                      {(q.options || []).map((opt, oi) => (
                        <div key={opt.id} className="vrt-survey__option-row">
                          <span className="vrt-survey__option-num">{oi + 1}.</span>
                          <input
                            type="text"
                            placeholder="Texte de la réponse"
                            value={opt.label || ''}
                            onChange={(e) => onOptionLabelChange(q.id, opt.id, e.target.value)}
                            className="vrt-input vrt-survey__option-input"
                          />
                          <button
                            type="button"
                            className="vrt-survey__icon-btn vrt-survey__icon-btn--danger"
                            onClick={() => onDeleteOption(q.id, opt.id)}
                            aria-label="Supprimer la réponse"
                            title="Supprimer la réponse"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="vrt-survey__add-opt"
                        onClick={() => onAddOption(q.id)}
                      >
                        <Plus size={16} /> Ajouter une réponse
                      </button>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}

        {addingQuestion ? (
          <div className="vrt-survey__card vrt-survey__card--new">
            <div className="vrt-survey__qhead">
              <span className="vrt-survey__qnum vrt-survey__qnum--new">Nouvelle</span>
              <TypeBadge type={newForm.type} />
            </div>

            <form onSubmit={onAddQuestion} className="vrt-survey__form">
              <textarea
                value={newForm.label}
                onChange={(e) => setNewForm(prev => ({ ...prev, label: e.target.value }))}
                className="vrt-survey__qlabel"
                rows="2"
                placeholder="Quelle est la question posée ?"
                required
                autoFocus
              />

              <div className="vrt-survey__qcontrols">
                <label className="vrt-survey__field">
                  <span>Type de réponse</span>
                  <select
                    value={newForm.type}
                    onChange={(e) => setNewForm(prev => ({ ...prev, type: e.target.value, options: prev.options.length ? prev.options : [''] }))}
                    className="vrt-input"
                  >
                    {TYPE_ORDER.map(t => (
                      <option key={t} value={t}>{TYPE_META[t].label}</option>
                    ))}
                  </select>
                  <small className="vrt-survey__hint">{TYPE_META[newForm.type].hint}</small>
                </label>

                <label className="vrt-survey__toggle">
                  <input
                    type="checkbox"
                    checked={newForm.required}
                    onChange={(e) => setNewForm(prev => ({ ...prev, required: e.target.checked }))}
                  />
                  <span>Réponse obligatoire</span>
                </label>

                <label className={`vrt-survey__toggle ${newForm.is_priority_flag ? 'vrt-survey__toggle--alert' : ''}`}>
                  <input
                    type="checkbox"
                    checked={newForm.is_priority_flag}
                    onChange={(e) => setNewForm(prev => ({ ...prev, is_priority_flag: e.target.checked }))}
                  />
                  <span>Déclencher l’alerte rouge sur la carte</span>
                </label>
              </div>

              {showOptionalTypes && (
                <div className="vrt-survey__options">
                  <div className="vrt-survey__options-title">Réponses possibles</div>
                  {newForm.options.map((opt, idx) => (
                    <div key={idx} className="vrt-survey__option-row">
                      <span className="vrt-survey__option-num">{idx + 1}.</span>
                      <input
                        type="text"
                        placeholder="Texte de la réponse"
                        value={opt}
                        onChange={(e) => onChangeNewOption(idx, e.target.value)}
                        className="vrt-input vrt-survey__option-input"
                      />
                      <button
                        type="button"
                        className="vrt-survey__icon-btn vrt-survey__icon-btn--danger"
                        onClick={() => onRemoveNewOption(idx)}
                        aria-label="Supprimer la réponse"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="vrt-survey__add-opt"
                    onClick={onAddNewOption}
                  >
                    <Plus size={16} /> Ajouter une réponse
                  </button>
                </div>
              )}

              <div className="vrt-survey__form-actions">
                <button type="submit" className="vrt-btn vrt-btn--teal">
                  Créer la question
                </button>
                <button
                  type="button"
                  className="vrt-btn vrt-btn--ghost"
                  onClick={() => {
                    setAddingQuestion(false);
                    setNewForm({ label: '', type: 'select', required: false, is_priority_flag: false, options: [''] });
                  }}
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        ) : (
          <button
            type="button"
            className="vrt-btn vrt-btn--teal vrt-survey__add-btn"
            onClick={() => setAddingQuestion(true)}
          >
            <Plus size={20} /> Ajouter une question
          </button>
        )}
      </main>

      <Footer />

      <style>{`
        .vrt-survey { min-height: 100vh; background: var(--vrt-bg-alt); }
        .vrt-survey__main { max-width: 880px; margin: 0 auto; padding: 24px 16px 80px; }

        .vrt-survey__top {
          display: flex; align-items: flex-start; justify-content: space-between;
          gap: 16px; margin-bottom: 24px;
        }
        .vrt-survey__title { margin: 0 0 4px; font-size: 22px; color: var(--vrt-navy); font-weight: 700; }
        .vrt-survey__lead { margin: 0; color: var(--vrt-text-muted); font-size: 14px; line-height: 1.5; max-width: 540px; }
        .vrt-survey__top-stats {
          display: flex; flex-direction: column; align-items: center;
          background: #fff; border: 1px solid var(--vrt-border); border-radius: var(--vrt-radius);
          padding: 8px 18px; flex-shrink: 0;
        }
        .vrt-survey__count { font-size: 24px; font-weight: 800; color: var(--vrt-navy); line-height: 1; }
        .vrt-survey__count-label { font-size: 11px; color: var(--vrt-text-muted); text-transform: uppercase; letter-spacing: 0.05em; margin-top: 2px; }

        .vrt-survey__error {
          display: flex; align-items: center; gap: 10px;
          background: #fdecea; color: #a23123; padding: 12px 14px;
          border-radius: 10px; margin-bottom: 16px; font-size: 14px;
          border: 1px solid #f5c6c2;
        }
        .vrt-survey__error-close {
          margin-left: auto; background: transparent; border: none; color: inherit;
          cursor: pointer; font-size: 16px; line-height: 1; padding: 4px 8px;
        }
        .vrt-survey__empty {
          padding: 40px 24px; text-align: center; color: var(--vrt-text-muted);
          background: #fff; border: 1px dashed var(--vrt-border); border-radius: var(--vrt-radius);
          font-size: 15px;
        }

        .vrt-survey__list { display: flex; flex-direction: column; gap: 14px; margin-bottom: 20px; }

        .vrt-survey__card {
          --accent: var(--vrt-teal);
          background: #fff;
          border: 1px solid var(--vrt-border);
          border-left: 4px solid var(--accent);
          border-radius: var(--vrt-radius);
          padding: 18px 20px 20px;
          display: flex; flex-direction: column; gap: 14px;
          transition: box-shadow .15s ease, border-color .15s ease;
        }
        .vrt-survey__card:hover { box-shadow: 0 2px 10px rgba(26,40,73,.06); }
        .vrt-survey__card--priority { background: linear-gradient(0deg, #fff8f8 0%, #fff 60%); }
        .vrt-survey__card--new { border-style: dashed; --accent: var(--vrt-teal); }

        .vrt-survey__qhead {
          display: flex; align-items: center; flex-wrap: wrap; gap: 8px;
        }
        .vrt-survey__qnum {
          display: inline-flex; align-items: center; justify-content: center;
          min-width: 36px; height: 26px; padding: 0 8px;
          background: var(--vrt-navy); color: #fff;
          border-radius: 6px; font-size: 12px; font-weight: 700; letter-spacing: 0.04em;
        }
        .vrt-survey__qnum--new { background: var(--vrt-teal); }
        .vrt-survey__type-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 4px 10px; border-radius: 999px; color: #fff;
          font-size: 11px; font-weight: 700; letter-spacing: 0.02em;
          text-transform: uppercase;
        }
        .vrt-survey__chip {
          display: inline-flex; align-items: center;
          padding: 4px 10px; border-radius: 999px;
          background: var(--vrt-bg-alt); color: var(--vrt-text-muted);
          font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em;
          border: 1px solid var(--vrt-border);
        }
        .vrt-survey__chip--alert { background: #fdecea; color: #c0392b; border-color: #f5c6c2; }

        .vrt-survey__qactions { margin-left: auto; display: flex; gap: 4px; }
        .vrt-survey__icon-btn {
          display: inline-flex; align-items: center; justify-content: center;
          width: 36px; height: 36px;
          background: transparent; border: 1px solid var(--vrt-border); border-radius: 8px;
          color: var(--vrt-text-muted); cursor: pointer;
          transition: background .12s ease, color .12s ease, border-color .12s ease;
        }
        .vrt-survey__icon-btn:hover:not(:disabled) { background: var(--vrt-bg-alt); color: var(--vrt-navy); border-color: var(--vrt-navy-300); }
        .vrt-survey__icon-btn:disabled { opacity: 0.35; cursor: not-allowed; }
        .vrt-survey__icon-btn--danger:hover:not(:disabled) { background: #fdecea; color: var(--vrt-danger); border-color: #f5c6c2; }

        .vrt-survey__qlabel {
          width: 100%;
          padding: 12px 14px; font-size: 17px; font-weight: 600; color: var(--vrt-navy);
          background: var(--vrt-bg-alt);
          border: 1px solid transparent;
          border-radius: 10px;
          font-family: inherit;
          line-height: 1.4;
          resize: vertical;
          min-height: 56px;
        }
        .vrt-survey__qlabel:hover { background: #fff; border-color: var(--vrt-border); }
        .vrt-survey__qlabel:focus { background: #fff; border-color: var(--vrt-teal); outline: none; }

        .vrt-survey__qcontrols {
          display: flex; flex-wrap: wrap; gap: 16px; align-items: flex-start;
        }
        .vrt-survey__field { flex: 1 1 240px; display: flex; flex-direction: column; gap: 6px; min-width: 200px; }
        .vrt-survey__field > span { font-size: 12px; color: var(--vrt-text-muted); font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; }
        .vrt-survey__field .vrt-input { min-height: 44px; }
        .vrt-survey__hint { font-size: 12px; color: var(--vrt-text-muted); font-style: italic; line-height: 1.4; }

        .vrt-survey__toggle {
          display: inline-flex; align-items: center; gap: 10px;
          padding: 10px 14px;
          background: var(--vrt-bg-alt); border: 1px solid var(--vrt-border);
          border-radius: 10px;
          font-size: 14px; color: var(--vrt-text); font-weight: 500;
          cursor: pointer;
          transition: all .12s ease;
          flex: 0 0 auto;
        }
        .vrt-survey__toggle:hover { background: #fff; border-color: var(--vrt-navy-300); }
        .vrt-survey__toggle input {
          width: 18px; height: 18px; accent-color: var(--vrt-teal);
          cursor: pointer; flex-shrink: 0;
        }
        .vrt-survey__toggle--alert {
          background: #fdecea; border-color: #f5c6c2; color: #a23123;
        }
        .vrt-survey__toggle--alert input { accent-color: #c0392b; }

        .vrt-survey__warning {
          display: flex; align-items: center; gap: 8px;
          padding: 10px 14px;
          background: #fff3cd; color: #7c5d00;
          border: 1px solid #ffe28a;
          border-radius: 8px;
          font-size: 13px; line-height: 1.4;
        }

        .vrt-survey__options {
          margin-top: 4px;
          padding-top: 16px;
          border-top: 1px dashed var(--vrt-border);
          display: flex; flex-direction: column; gap: 8px;
        }
        .vrt-survey__options-title {
          font-size: 12px; color: var(--vrt-text-muted); font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px;
        }
        .vrt-survey__options-empty {
          font-size: 13px; color: var(--vrt-text-muted); font-style: italic; padding: 4px 0;
        }
        .vrt-survey__option-row {
          display: flex; align-items: center; gap: 10px;
        }
        .vrt-survey__option-num {
          width: 24px; flex-shrink: 0; color: var(--vrt-text-muted);
          font-size: 14px; font-weight: 600; text-align: right;
        }
        .vrt-survey__option-input {
          flex: 1; min-height: 44px; font-size: 15px;
        }
        .vrt-survey__add-opt {
          display: inline-flex; align-items: center; gap: 6px;
          align-self: flex-start; margin-top: 4px;
          padding: 8px 14px; background: transparent;
          border: 1px dashed var(--vrt-teal); border-radius: 8px;
          color: var(--vrt-teal-700); font-weight: 600; font-size: 13px;
          cursor: pointer;
          transition: all .12s ease;
        }
        .vrt-survey__add-opt:hover { background: var(--vrt-teal-100); border-style: solid; }

        .vrt-survey__form { display: flex; flex-direction: column; gap: 14px; }
        .vrt-survey__form-actions { display: flex; gap: 10px; padding-top: 4px; }
        .vrt-survey__form-actions .vrt-btn { flex: 1 1 auto; }

        .vrt-survey__add-btn {
          align-self: stretch; margin-top: 8px;
          width: 100%;
          font-size: 16px; min-height: 60px;
        }

        @media (max-width: 540px) {
          .vrt-survey__main { padding: 16px 12px 60px; }
          .vrt-survey__card { padding: 14px 14px 16px; border-left-width: 3px; }
          .vrt-survey__qhead { gap: 6px; }
          .vrt-survey__qnum { min-width: 32px; height: 24px; font-size: 11px; }
          .vrt-survey__type-badge, .vrt-survey__chip { font-size: 10px; padding: 3px 8px; }
          .vrt-survey__qlabel { font-size: 16px; }
          .vrt-survey__toggle { width: 100%; flex: 1 1 100%; }
          .vrt-survey__top { flex-direction: column; }
          .vrt-survey__top-stats { flex-direction: row; gap: 8px; align-items: baseline; padding: 6px 14px; align-self: flex-start; }
          .vrt-survey__count { font-size: 18px; }
        }
      `}</style>
    </div>
  );
}
